import {
    Image,
    ImageMetadata,
    ImageStatus,
    ImageVisibility,
} from 'images/modules/ImagesModule/ImagesModule.types';
import R from 'ramda';
import { MeasureInformationHelper } from 'utils/MeasureInformationHelper/MeasureInformationHelper';

import { ActivityCircleColor } from 'reactComponents/icons/activityCircle';

const getSizeInBytes = (image: Image) => image.size || 0;

function getSizeInGb(image: Image): null | number {
    if (typeof image.size !== 'number' && typeof image.min_disk !== 'number') {
        return null;
    }
    const imageSizeInGb =
        image.size && Math.ceil(MeasureInformationHelper.bytesToGB(image.size));
    const imageMinDiskInGb = image.min_disk;
    return Math.max(imageMinDiskInGb || 0, imageSizeInGb || 0);
}

const getIsWindows = (image) => {
    return checkOperatingSystem(image, 'win');
};

const getIsBitrix = (image) => {
    return checkOperatingSystem(image, 'bitrix');
};

function checkOperatingSystem(
    image: Image | ImageMetadata,
    osName: 'win' | 'bitrix',
) {
    return (
        image &&
        // @ts-ignore
        [image.os_distro, image.name, image.image_name].some((text) =>
            (text || '').toLowerCase().includes(osName),
        )
    );
}

const getHasGuestAgent: (image: Image | ImageMetadata) => boolean = R.allPass([
    R.propEq('hw_qemu_guest_agent', 'yes'),
    R.propEq('os_require_quiesce', 'yes'),
]);

function getIsAbleToBackup(image: Image | ImageMetadata) {
    const hasGuestAgent = getHasGuestAgent(image);
    const hasExcludingSid = ['magnum', 'trove', 'sahara'].includes(image.sid!);
    return hasGuestAgent && !hasExcludingSid;
}

type License = { id: string; name: string; factor: number };
function getImageLicenses(image: Image | ImageMetadata, vcpus: number) {
    const licenses: License[] = [];
    const isWindows = getIsWindows(image);
    const salCount = image['mcs:lic:mssqlsal']; // списание за SAL (кол-во лицензий)

    if (isWindows) {
        licenses.push({ id: 'msrds', name: 'RDS', factor: 1 });
    }

    if (image['mcs:lic:mssql']) {
        licenses.push({
            id: 'mssql',
            name: 'SQL Server Standard',
            factor: Math.round(vcpus / 2),
        });
    }

    if (image['mcs:lic:mssqle']) {
        licenses.push({
            id: 'mssqle',
            name: 'SQL Server Enterprise',
            factor: Math.round(vcpus / 2),
        });
    }

    if (salCount) {
        licenses.push({
            id: 'mssqlsal',
            name: 'SQL Server Standard per SAL',
            factor: salCount,
        });
    }

    return licenses;
}

function getTextByStatus(status: ImageStatus) {
    return statusToText[status];
}

function getColorByStatus(status: ImageStatus): ActivityCircleColor {
    switch (status) {
        case 'active':
            return 'green';
        case 'queued':
        case 'importing':
        case 'saving':
        case 'uploading':
            return 'blue';
        case 'deactivated':
        case 'deleted':
        case 'killed':
        case 'pending_delete':
        default:
            return 'red';
    }
}

function getIsProcessing(status: ImageStatus) {
    switch (status) {
        case 'queued':
        case 'saving':
        case 'uploading':
        case 'importing':
            return true;
        default:
            return false;
    }
}

const visibilityToText: Record<ImageVisibility, string> = {
    public: 'Публичный',
    private: 'Частный',
    shared: 'Shared',
    community: 'Community',
};

const statusToText: Record<ImageStatus, string> = {
    queued: 'В очереди',
    saving: 'Сохранение данных для образа',
    active: 'Активен',
    uploading: 'Загрузка',
    importing: 'Импорт',
    killed: 'Ошибка загрузки данных',
    deleted: 'Удален',
    pending_delete: 'Удален, не подлежит восстановлению',
    deactivated: 'Не доступен',
};

export const ImagesUtils = {
    getColorByStatus,
    getIsProcessing,
    getSizeInBytes,
    getSizeInGb,
    getIsWindows,
    getIsBitrix,
    getTextByStatus,
    visibilityToText,
    statusToText,
    getIsAbleToBackup,
    getImageLicenses,
};
